import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useRazorpay } from "react-razorpay";
import { SubscriptionDetailsPageContainer } from "./SubscriptionDetailsPageContainer";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import { axiosFunction } from "../../axiosFunction";
import { smaartCardUrl } from "../../constant";
import { toastMessage } from "../../utils/toastUtils";
import { swalUtils } from "../../utils/swalUtils";
import { setUserData } from "../../store/User/UserSlice";
import defaultPhoto from "../../assets/images/imagePlaceholder.png";
import logo from "../../assets/images/logo.png";
import {
  FaCalendarAlt,
  FaUsers,
  FaBuilding,
  FaCheckCircle,
  FaShareAlt,
  FaArrowLeft,
  FaStar,
  FaLock,
  FaSpinner,
  FaUserCheck,
  FaExclamationTriangle,
} from "react-icons/fa";

const SubscriptionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { Razorpay } = useRazorpay();

  const userData = useSelector((state) => state.userData);
  const loggedInUserId = localStorage.getItem("userId");
  const isLoggedIn = Boolean(loggedInUserId);

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Registration & OTP States
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [formName, setFormName] = useState("");
  const [formMobile, setFormMobile] = useState("");
  const [formState, setFormState] = useState("");
  const [formCity, setFormCity] = useState("");
  const [formOtp, setFormOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedUserData, setVerifiedUserData] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [buyingSubscription, setBuyingSubscription] = useState(false);
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [existingPatient, setExistingPatient] = useState(null);
  const [activeSubscriptionInfo, setActiveSubscriptionInfo] = useState(null);

  const [formErrors, setFormErrors] = useState({
    name: "",
    mobile: "",
    state: "",
    city: "",
    otp: "",
  });

  // Countdown timer for Resend OTP
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Check active subscription for logged-in user on mount
  useEffect(() => {
    const checkLoggedInUserSub = async () => {
      if (!loggedInUserId) return;
      try {
        const subListRes = await axiosFunction(
          `${smaartCardUrl}/api/v1/dfordr/subcription/subscriptionByPatientList/${loggedInUserId}`,
          "GET"
        );
        const subList = Array.isArray(subListRes?.data?.response)
          ? subListRes.data.response
          : [];

        const activeSub = subList.find((sub) => {
          if (!sub || sub.isDeleted === 1) return false;
          if (sub.currentStatus === 0 || sub.currentStatus === "0") return true;
          if (sub.endTime) {
            const end = new Date(sub.endTime);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return end >= today;
          }
          return false;
        });

        if (activeSub) {
          setActiveSubscriptionInfo(activeSub);
        }
      } catch (err) {
        console.error("Error checking logged in user subscription:", err);
      }
    };

    if (isLoggedIn && loggedInUserId) {
      checkLoggedInUserSub();
    }
  }, [isLoggedIn, loggedInUserId]);

  // Fetch States on mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axiosFunction(
          `${smaartCardUrl}/location/api/allStates`,
          "GET"
        );
        if (res?.data && Array.isArray(res.data)) {
          setStates(res.data);
          // Default Gujarat if available (stateCode: "24")
          const guj = res.data.find((s) => String(s.stateCode) === "24");
          if (guj) {
            setFormState("24");
            const cityRes = await axiosFunction(
              `${smaartCardUrl}/location/api/city/24`,
              "GET"
            );
            if (cityRes?.data && Array.isArray(cityRes.data)) {
              setCities(cityRes.data);
            }
          }
        }
      } catch (err) {
        console.error("Error loading states:", err);
      }
    };
    fetchStates();
  }, []);

  // Handle State change
  const handleStateChange = async (e) => {
    const val = e.target.value;
    setFormState(val);
    setFormCity("");
    setCities([]);
    setFormErrors((prev) => ({ ...prev, state: "", city: "" }));

    if (val) {
      try {
        const cityRes = await axiosFunction(
          `${smaartCardUrl}/location/api/city/${val}`,
          "GET"
        );
        if (cityRes?.data && Array.isArray(cityRes.data)) {
          setCities(cityRes.data);
        }
      } catch (err) {
        console.error("Error loading cities:", err);
      }
    }
  };

  // Fetch Subscription Details
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      setLoading(true);
      try {
        // 1. Try single subscription details API
        const directRes = await axiosFunction(
          `${smaartCardUrl}/api/v1/dfordr/general/getSubscriptionPlanDetails/${id}`,
          "GET",
          null
        );

        if (directRes?.data?.status && directRes?.data?.response) {
          setSubscription(directRes.data.response);
          setLoading(false);
          return;
        }

        // 2. Try subscription master get by id
        const masterRes = await axiosFunction(
          `${smaartCardUrl}/api/v1/dfordr/master/subcription/get/${id}`,
          "GET",
          null
        );

        if (masterRes?.data?.status && masterRes?.data?.response) {
          setSubscription(masterRes.data.response);
          setLoading(false);
          return;
        }

        // 3. Fallback to fetchall
        const res = await axiosFunction(
          `${smaartCardUrl}/api/v1/dfordr/master/subcription/fetchall`,
          "GET",
          null
        );

        if (res?.data?.status && Array.isArray(res?.data?.response)) {
          const found = res.data.response.find(
            (sub) =>
              String(sub.subscriptionMasterId) === String(id) ||
              String(sub.id) === String(id)
          );
          if (found) {
            setSubscription(found);
            setLoading(false);
            return;
          }
        }

        setSubscription(null);
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSubscriptionDetails();
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleCopyLink = () => {
    const subId = subscription?.subscriptionMasterId || subscription?.id || id;
    const link = `${window.location.origin}/subcriptiondetails/${subId}`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(link);
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    }
    setCopied(true);
    toastMessage("Subscription link copied to clipboard!", "success");
    setTimeout(() => setCopied(false), 3000);
  };

  // Price calculations
  const numPrice = Number(subscription?.price || 0);
  const numDiscount = Number(subscription?.discountPrice || 0);

  let finalPrice = numPrice;
  let originalPrice = null;
  let discountAmount = 0;

  if (numDiscount > 0 && numDiscount !== numPrice) {
    if (numDiscount < numPrice) {
      finalPrice = numDiscount;
      originalPrice = numPrice;
      discountAmount = numPrice - numDiscount;
    } else {
      finalPrice = numPrice;
      originalPrice = numDiscount;
      discountAmount = numDiscount - numPrice;
    }
  }

  // Check if mobile number exists and if subscription is active or expired
  const checkMobileNumberStatus = async (
    mobileNumber,
    currentStatesList = states
  ) => {
    const cleanMobile = String(mobileNumber || "").trim();
    if (!cleanMobile || cleanMobile.length !== 10) {
      setExistingPatient(null);
      setActiveSubscriptionInfo(null);
      return null;
    }

    setCheckingMobile(true);

    try {
      let exists = false;
      let isActiveSub = false;
      let patient = null;
      let activeSub = null;

      // 1. Try unified endpoint
      const unifiedRes = await axiosFunction(
        `${smaartCardUrl}/api/v1/dfordr/general/checkUserSubscriptionStatus?mobileNo=${cleanMobile}`,
        "GET"
      );

      if (unifiedRes?.data?.status && unifiedRes?.data?.response) {
        exists = unifiedRes.data.response.exists;
        isActiveSub = unifiedRes.data.response.isActiveSubscription;
        patient = unifiedRes.data.response.patient;
        activeSub = unifiedRes.data.response.activeSubscription;
      } else {
        // Fallback: checkMobileNumber + subscriptionByPatientList
        const mobRes = await axiosFunction(
          `${smaartCardUrl}/api/v1/dfordr/general/checkMobileNumber?mobileNo=${cleanMobile}`,
          "GET"
        );

        if (mobRes?.data?.status && mobRes?.data?.response) {
          exists = true;
          patient = mobRes.data.response;

          try {
            const listRes = await axiosFunction(
              `${smaartCardUrl}/api/v1/dfordr/subcription/subscriptionByPatientList/${patient.patientId}`,
              "GET"
            );
            const subList = Array.isArray(listRes?.data?.response)
              ? listRes.data.response
              : [];

            activeSub = subList.find((sub) => {
              if (!sub || sub.isDeleted === 1) return false;
              if (sub.currentStatus === 0 || sub.currentStatus === "0") return true;
              if (sub.endTime) {
                const end = new Date(sub.endTime);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return end >= today;
              }
              return false;
            });

            if (!activeSub) {
              const singleRes = await axiosFunction(
                `${smaartCardUrl}/api/v1/dfordr/subcription/subscriptionByPatient/${patient.patientId}`,
                "GET"
              );
              const single = singleRes?.data?.response;
              if (
                single &&
                single.isDeleted !== 1 &&
                (single.currentStatus === 0 ||
                  single.currentStatus === "0" ||
                  (single.endTime &&
                    new Date(single.endTime) >= new Date().setHours(0, 0, 0, 0)))
              ) {
                activeSub = single;
              }
            }

            if (activeSub) {
              isActiveSub = true;
            }
          } catch (subErr) {
            console.error("Error checking subscriptions fallback:", subErr);
          }
        }
      }

      if (isActiveSub && activeSub) {
        // CASE 2: Subscription is ALREADY ACTIVE!
        setActiveSubscriptionInfo(activeSub);
        setExistingPatient(patient);
        toastMessage(
          `Subscription is already active for this mobile number! (${
            activeSub.subscriptionMasterName || "Active Plan"
          })`,
          "warning"
        );
        return { isExisting: true, isActive: true, patient, activeSub };
      }

      if (exists && patient) {
        // CASE 1: Number is registered AND subscription is EXPIRED!
        setActiveSubscriptionInfo(null);
        setExistingPatient(patient);

        if (patient.name) {
          setFormName(patient.name);
        }

        const stList = currentStatesList?.length > 0 ? currentStatesList : states;
        let matchedStateCode = "";
        if (patient.state && stList?.length > 0) {
          const matched = stList.find(
            (s) =>
              s.stateName?.toLowerCase().trim() === patient.state.toLowerCase().trim() ||
              String(s.stateCode) === String(patient.state)
          );
          if (matched) {
            matchedStateCode = String(matched.stateCode);
            setFormState(matchedStateCode);
          }
        }

        if (matchedStateCode) {
          try {
            const cityRes = await axiosFunction(
              `${smaartCardUrl}/location/api/city/${matchedStateCode}`,
              "GET"
            );
            if (cityRes?.data && Array.isArray(cityRes.data)) {
              setCities(cityRes.data);
              if (patient.city) {
                setFormCity(patient.city);
              }
            }
          } catch (cErr) {
            console.error("Error loading cities for auto-fill:", cErr);
          }
        } else if (patient.city) {
          setFormCity(patient.city);
        }

        setFormErrors({
          name: "",
          mobile: "",
          state: "",
          city: "",
          otp: "",
        });

        toastMessage("Existing account found! Details auto-filled.", "info");
        return { isExisting: true, isActive: false, patient, activeSub: null };
      } else {
        setExistingPatient(null);
        setActiveSubscriptionInfo(null);
        return { isExisting: false, isActive: false, patient: null, activeSub: null };
      }
    } catch (err) {
      console.error("Error in checkMobileNumberStatus:", err);
      return { isExisting: false, isActive: false, patient: null, activeSub: null };
    } finally {
      setCheckingMobile(false);
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (activeSubscriptionInfo) {
      toastMessage(
        "Subscription is already active for this mobile number!",
        "warning"
      );
      return;
    }

    if (!formMobile.trim() || !/^[6-9]\d{9}$/.test(formMobile.trim())) {
      setFormErrors((prev) => ({
        ...prev,
        mobile: "Please enter valid 10-digit mobile number",
      }));
      return;
    }

    setSendingOtp(true);

    let checkResult = null;
    try {
      checkResult = await checkMobileNumberStatus(formMobile.trim());
    } catch (e) {
      console.error(e);
    }

    if (checkResult?.isActive) {
      setSendingOtp(false);
      toastMessage(
        "Subscription is already active for this mobile number!",
        "warning"
      );
      return;
    }

    const currentName = formName.trim() || checkResult?.patient?.name || "";
    const currentState = formState || checkResult?.patient?.state || "";
    const currentCity = formCity || checkResult?.patient?.city || "";

    if (!currentName || currentName.length < 2) {
      setFormErrors((prev) => ({
        ...prev,
        name: "Please enter valid name (min 2 characters)",
      }));
      setSendingOtp(false);
      return;
    }

    if (!currentState) {
      setFormErrors((prev) => ({
        ...prev,
        state: "Please select state",
      }));
      setSendingOtp(false);
      return;
    }

    if (!currentCity) {
      setFormErrors((prev) => ({
        ...prev,
        city: "Please select city",
      }));
      setSendingOtp(false);
      return;
    }

    const selectedStateObj = states.find(
      (s) => String(s.stateCode) === String(currentState)
    );
    const stateName =
      selectedStateObj?.stateName ||
      (typeof currentState === "string" ? currentState : "Gujarat");

    try {
      const res = await axiosFunction(
        `${smaartCardUrl}/api/v1/dfordr/general/otp/user/send`,
        "POST",
        {
          name: currentName,
          phone: formMobile.trim(),
          state: stateName,
          city: currentCity,
        }
      );

      if (res?.data?.status) {
        setOtpSent(true);
        setCountdown(30);
        setFormOtp("");
        toastMessage(res?.data?.message || "OTP sent successfully!", "success");
      } else {
        toastMessage(res?.data?.message || "Failed to send OTP", "error");
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
      toastMessage("Failed to send OTP", "error");
    } finally {
      setSendingOtp(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return;
    try {
      const res = await axiosFunction(
        `${smaartCardUrl}/api/v1/dfordr/general/otp/send`,
        "POST",
        { phone: formMobile.trim() }
      );
      if (res?.data?.status) {
        setCountdown(30);
        setFormOtp("");
        toastMessage(res?.data?.message || "OTP resent successfully!", "success");
      } else {
        toastMessage(res?.data?.message || "Failed to resend OTP", "error");
      }
    } catch (err) {
      console.error("Error resending OTP:", err);
      toastMessage("Failed to resend OTP", "error");
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!formOtp.trim() || formOtp.trim().length < 4) {
      setFormErrors((prev) => ({
        ...prev,
        otp: "Please enter valid OTP",
      }));
      return;
    }

    setVerifyingOtp(true);
    const selectedStateObj = states.find(
      (s) => String(s.stateCode) === String(formState)
    );
    const stateName = selectedStateObj?.stateName || "Gujarat";

    try {
      const verifyRes = await axiosFunction(
        `${smaartCardUrl}/api/v1/dfordr/general/verifyOtpRegistration`,
        "POST",
        {
          name: formName.trim(),
          phone: formMobile.trim(),
          otp: formOtp.trim(),
          state: stateName,
          city: formCity,
        }
      );

      if (verifyRes?.data?.status) {
        const userObj = verifyRes?.data?.response;
        setVerifiedUserData(userObj);
        setIsVerified(true);
        toastMessage(
          "Mobile verified successfully! Subscription is now available.",
          "success"
        );
      } else {
        toastMessage(verifyRes?.data?.message || "Invalid OTP", "error");
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      toastMessage("Failed to verify OTP", "error");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Reset verification to edit details
  const handleEditDetails = () => {
    setOtpSent(false);
    setIsVerified(false);
    setVerifiedUserData(null);
    setFormOtp("");
  };

  // Subscribe Now & Razorpay flow
  const handleSubscribe = async () => {
    if (!subscription) return;

    // Check if user is logged in OR verified
    let currentPatientId = localStorage.getItem("userId");
    let currentName = userData?.patientName || userData?.name;
    let currentPhone = userData?.mobileNumber || userData?.phone;

    if (!currentPatientId) {
      if (!isVerified || !verifiedUserData?.patientId) {
        toastMessage(
          "Please fill details and verify OTP first to subscribe",
          "warning"
        );
        return;
      }
      currentPatientId = verifiedUserData.patientId;
      currentName = verifiedUserData.name || formName;
      currentPhone = verifiedUserData.phone || formMobile;
    }

    if (!currentPatientId) {
      toastMessage("User details not found. Please verify again.", "error");
      return;
    }

    const buyNowSwal = await swalUtils(
      "Confirm Subscription",
      `Plan: ${subscription?.name} Membership`,
      `Buy now for ₹${finalPrice}`,
      "Cancel"
    );

    if (!buyNowSwal?.isConfirmed) return;

    setBuyingSubscription(true);

    try {
      const paymentPayload = {
        patientId: currentPatientId,
        subscriptionId: subscription?.subscriptionMasterId,
        amount: finalPrice * 100,
        price: finalPrice,
      };

      const paymentRes = await axiosFunction(
        `${smaartCardUrl}/api/v1/payment/create`,
        "POST",
        paymentPayload
      );

      if (paymentRes?.data?.status) {
        const options = {
          key: paymentRes?.data?.response?.keyId,
          amount: finalPrice * 100,
          currency: "INR",
          name: "Smaart Card",
          description: `Subscription: ${subscription?.name} Membership`,
          image: subscription?.img || logo,
          order_id: paymentRes?.data?.response?.orderId,
          handler: async function (paymentResponse) {
            const updatePayload = {
              paymentId: paymentRes?.data?.response?.paymentId,
              razorpayPaymentId: paymentResponse?.razorpay_payment_id,
              razorpayOrderId: paymentResponse?.razorpay_order_id,
              razorpaySignature: paymentResponse?.razorpay_signature,
              status: "success",
            };

            const razorPayRes = await axiosFunction(
              `${smaartCardUrl}/api/v1/payment/update`,
              "POST",
              updatePayload
            );

            if (razorPayRes?.data?.status) {
              // Now user is considered registered & logged in!
              const finalUser = verifiedUserData || {
                patientId: currentPatientId,
                name: currentName,
                phone: currentPhone,
              };

              dispatch(setUserData(finalUser));
              localStorage.setItem("userId", currentPatientId);
              localStorage.setItem("role", "user");
              if (formCity) localStorage.setItem("userCity", formCity);
              const selectedStateObj = states.find(
                (s) => String(s.stateCode) === String(formState)
              );
              if (selectedStateObj) {
                localStorage.setItem("userState", selectedStateObj.stateName);
              }

              await swalUtils(
                "Payment Successful!",
                `Welcome to ${subscription?.name} Membership! Your subscription is now active.`,
                "Go to My Profile",
                null
              );
              navigate("/myprofile");
            } else {
              toastMessage("Payment confirmation failed on server!", "error");
            }
          },
          prefill: {
            name: currentName,
            contact: currentPhone,
          },
          theme: {
            color: "#024089",
          },
        };

        const rzp = new Razorpay(options);
        rzp.open();
        rzp.on("payment.failed", async function (response) {
          try {
            const failPayload = {
              paymentId: paymentRes?.data?.response?.paymentId,
              status: "failed",
              code: response?.error?.code,
              reason: response?.error?.reason,
            };
            await axiosFunction(
              `${smaartCardUrl}/api/v1/payment/update`,
              "POST",
              failPayload
            );
            toastMessage("Payment was cancelled or failed", "error");
          } catch (err) {
            console.error("Payment failure recording error:", err);
          }
        });
      } else {
        toastMessage(
          paymentRes?.data?.message || "Failed to initiate payment",
          "error"
        );
      }
    } catch (err) {
      console.error("Error initiating payment:", err);
      toastMessage("Failed to connect to payment gateway", "error");
    } finally {
      setBuyingSubscription(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  // Can user buy? (Yes if already logged in OR if OTP verified)
  const canBuy = isLoggedIn || isVerified;

  return (
    <SubscriptionDetailsPageContainer>
      <Header />

      <div className="allContent">
        <Container>
          {/* Breadcrumb Navigation */}
          <div className="breadcrumb-nav d-flex align-items-center justify-content-between">
            <div>
              <Link to="/">Home</Link> &nbsp;/&nbsp; <span>Subscriptions</span> &nbsp;/&nbsp;
              <span className="text-dark fw-bold">
                {subscription ? `${subscription.name} Membership` : "Details"}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn-share-top"
                onClick={handleCopyLink}
                title="Copy Link to share this subscription"
              >
                {copied ? (
                  <>
                    <FaCheckCircle color="#28a745" /> Copied!
                  </>
                ) : (
                  <>
                    <FaShareAlt /> Share / Copy Link
                  </>
                )}
              </button>
              <button className="btn-back" onClick={() => navigate(-1)}>
                <FaArrowLeft size={12} /> Back
              </button>
            </div>
          </div>

          {!subscription ? (
            <div className="not-found-card">
              <h3 className="mb-3 text-secondary">Subscription Not Found</h3>
              <p className="text-muted mb-4">
                The subscription plan you are looking for may have been removed or does not exist.
              </p>
              <button
                className="btn btn-primary px-4 py-2"
                style={{ borderRadius: "8px" }}
                onClick={() => navigate("/")}
              >
                Browse All Subscriptions
              </button>
            </div>
          ) : (
            <Row className="gy-4 align-items-start">
              {/* Left Column: Subscription Details */}
              <Col lg={7} md={12}>
                <div className="subscription-left-card p-4">
                  {/* Plan Header: Image + Title + Status */}
                  <div className="plan-header-banner mb-4">
                    <div className="plan-image-box">
                      <img
                        src={subscription?.img || defaultPhoto}
                        alt={subscription?.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultPhoto;
                        }}
                      />
                    </div>
                    <div className="plan-header-info">
                      <span
                        className={`subscription-badge ${
                          subscription.isActive === 0 ? "badge-active" : "badge-available"
                        }`}
                      >
                        <FaCheckCircle size={12} />
                        {subscription.isActive === 0 ? "Active Plan" : "Available"}
                      </span>
                      <h1 className="plan-title">{subscription.name} Membership</h1>
                    </div>
                  </div>

                  {/* Pricing Box */}
                  <div className="pricing-box mb-4">
                    <span className="current-price">₹{finalPrice}</span>
                    <span className="period">
                      / {subscription.durationInDays ? `${subscription.durationInDays} Days` : "month"}
                    </span>

                    {originalPrice && (
                      <>
                        <span className="original-price">₹{originalPrice}</span>
                        <span className="save-badge">Save ₹{discountAmount}</span>
                      </>
                    )}
                  </div>

                  {/* Feature Pills Grid */}
                  <div className="feature-pills-grid">
                    <div className="feature-pill-item">
                      <div className="pill-icon">
                        <FaCalendarAlt />
                      </div>
                      <div className="pill-content">
                        <div className="pill-label">Validity</div>
                        <div className="pill-value">{subscription.durationInDays || 30} Days</div>
                      </div>
                    </div>

                    <div className="feature-pill-item">
                      <div className="pill-icon">
                        <FaUsers />
                      </div>
                      <div className="pill-content">
                        <div className="pill-label">Family Members</div>
                        <div className="pill-value">
                          {subscription.maxFamilyMember && Number(subscription.maxFamilyMember) > 0
                            ? `Up to ${subscription.maxFamilyMember} Members`
                            : "Individual (Self)"}
                        </div>
                      </div>
                    </div>

                    <div className="feature-pill-item">
                      <div className="pill-icon">
                        <FaBuilding />
                      </div>
                      <div className="pill-content">
                        <div className="pill-label">Partner / Brand</div>
                        <div className="pill-value">
                          {subscription.partnerName || "Smaart Card Partner"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Features & Benefits */}
                  <div className="key-features-card">
                    <div className="features-heading">
                      <FaStar color="#f59e0b" />
                      <span>Key Features & Benefits</span>
                    </div>
                    {subscription.keyFeatures && subscription.keyFeatures.trim() !== "" ? (
                      <div
                        className="features-body"
                        dangerouslySetInnerHTML={{ __html: subscription.keyFeatures }}
                      />
                    ) : (
                      <div className="features-body">
                        <ul className="list-unstyled mb-0" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          <li className="d-flex align-items-center gap-2">
                            <FaCheckCircle color="#2e7d32" size={15} style={{ flexShrink: 0 }} />
                            <span>Access to exclusive member discounts across verified partner merchants</span>
                          </li>
                          <li className="d-flex align-items-center gap-2">
                            <FaCheckCircle color="#2e7d32" size={15} style={{ flexShrink: 0 }} />
                            <span>Instant digital card issuance & seamless online verification</span>
                          </li>
                          <li className="d-flex align-items-center gap-2">
                            <FaCheckCircle color="#2e7d32" size={15} style={{ flexShrink: 0 }} />
                            <span>Valid for {subscription.durationInDays || 30} days from the date of activation</span>
                          </li>
                          <li className="d-flex align-items-center gap-2">
                            <FaCheckCircle color="#2e7d32" size={15} style={{ flexShrink: 0 }} />
                            <span>Dedicated priority customer support & partner assistance</span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Right Column: Sticky User Registration & Verification Card */}
              <Col lg={5} md={12}>
                <div className="sticky-right-card">
                  <div className="registration-card p-4">
                    <div className="reg-header">
                      <h2 className="reg-title">User Registration</h2>
                      <p className="reg-subtitle">
                        {isLoggedIn
                          ? "Your account is active. Confirm your subscription below."
                          : isVerified
                          ? "Mobile verified! Complete payment to activate membership."
                          : "Fill details & verify OTP to buy this subscription"}
                      </p>
                    </div>

                    {isLoggedIn ? (
                      <div className="logged-in-box mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2 text-primary fw-bold">
                          <FaUserCheck size={18} color="#024089" />
                          <span>Logged In User</span>
                        </div>
                        <div className="small text-dark mb-1">
                          <strong>Name:</strong> {userData?.patientName || userData?.name || "Member"}
                        </div>
                        <div className="small text-dark">
                          <strong>Mobile:</strong> {userData?.mobileNumber || userData?.phone}
                        </div>
                      </div>
                    ) : isVerified ? (
                      <div className="verified-banner mb-3">
                        <div className="verified-info">
                          <FaCheckCircle className="verified-icon" />
                          <div className="verified-text">
                            <div>
                              {verifiedUserData?.name || formName} (
                              {verifiedUserData?.phone || formMobile})
                            </div>
                            <div className="verified-sub">
                              ✓ Mobile verified! Ready to complete subscription payment.
                            </div>
                          </div>
                        </div>
                        <button className="btn-reedit" onClick={handleEditDetails}>
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        {activeSubscriptionInfo && (
                          <div
                            className="mb-3 p-3"
                            style={{
                              backgroundColor: "#fffbeb",
                              border: "1px solid #fde68a",
                              borderRadius: "10px",
                              color: "#92400e",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 fw-bold mb-1">
                              <FaExclamationTriangle color="#d97706" size={16} />
                              <span>Subscription Already Active!</span>
                            </div>
                            <div style={{ fontSize: "12.5px", lineHeight: "1.4" }}>
                              This mobile number already has an active subscription (
                              <strong>
                                {activeSubscriptionInfo.subscriptionMasterName || "Membership"}
                              </strong>
                              ) valid till{" "}
                              <strong>{activeSubscriptionInfo.endTime}</strong>. You cannot purchase another subscription while one is active.
                            </div>
                          </div>
                        )}

                        {/* Name Input */}
                        <div className="form-group">
                          <label>Full Name *</label>
                          <input
                            type="text"
                            placeholder="Enter Full Name"
                            value={formName}
                            onChange={(e) => {
                              setFormName(e.target.value);
                              setFormErrors((prev) => ({ ...prev, name: "" }));
                            }}
                            disabled={otpSent}
                          />
                          {formErrors.name && (
                            <div className="field-error">{formErrors.name}</div>
                          )}
                        </div>

                        {/* Mobile No. Input */}
                        <div className="form-group">
                          <div className="d-flex justify-content-between align-items-center">
                            <label className="mb-0">Mobile Number *</label>
                            {checkingMobile && (
                              <small className="text-primary" style={{ fontSize: "11px" }}>
                                <FaSpinner className="spinner-border spinner-border-sm me-1" /> Checking number...
                              </small>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Enter 10-digit Mobile No."
                            maxLength={10}
                            className="mt-1"
                            value={formMobile}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "");
                              setFormMobile(val);
                              setFormErrors((prev) => ({ ...prev, mobile: "" }));
                              if (val.length === 10) {
                                checkMobileNumberStatus(val);
                              } else {
                                setExistingPatient(null);
                                setActiveSubscriptionInfo(null);
                              }
                            }}
                            onBlur={() => {
                              if (formMobile.length === 10 && !existingPatient && !activeSubscriptionInfo) {
                                checkMobileNumberStatus(formMobile);
                              }
                            }}
                            disabled={otpSent}
                          />
                          {existingPatient && !activeSubscriptionInfo && (
                            <small className="text-success fw-bold d-block mt-1" style={{ fontSize: "11px" }}>
                              <FaCheckCircle className="me-1" /> Existing user found ({existingPatient.name}) - Details auto-filled
                            </small>
                          )}
                          {activeSubscriptionInfo && (
                            <small className="text-danger fw-bold d-block mt-1" style={{ fontSize: "11px" }}>
                              <FaExclamationTriangle className="me-1" /> Plan active till {activeSubscriptionInfo.endTime}
                            </small>
                          )}
                          {formErrors.mobile && (
                            <div className="field-error">{formErrors.mobile}</div>
                          )}
                        </div>

                        {/* State & City Dropdowns */}
                        <Row className="g-2">
                          <Col sm={6}>
                            <div className="form-group">
                              <label>Select State *</label>
                              <select
                                value={formState}
                                onChange={handleStateChange}
                                disabled={otpSent}
                              >
                                <option value="">Select State</option>
                                {states.map((st) => (
                                  <option key={st.stateId} value={st.stateCode}>
                                    {st.stateName}
                                  </option>
                                ))}
                              </select>
                              {formErrors.state && (
                                <div className="field-error">{formErrors.state}</div>
                              )}
                            </div>
                          </Col>
                          <Col sm={6}>
                            <div className="form-group">
                              <label>Select City *</label>
                              <select
                                value={formCity}
                                onChange={(e) => {
                                  setFormCity(e.target.value);
                                  setFormErrors((prev) => ({ ...prev, city: "" }));
                                }}
                                disabled={otpSent || !formState}
                              >
                                <option value="">Select City</option>
                                {cities.map((ct) => (
                                  <option key={ct.cityId} value={ct.cityName}>
                                    {ct.cityName}
                                  </option>
                                ))}
                              </select>
                              {formErrors.city && (
                                <div className="field-error">{formErrors.city}</div>
                              )}
                            </div>
                          </Col>
                        </Row>

                        {/* Send OTP button (if not sent yet) */}
                        {!otpSent ? (
                          <button
                            type="button"
                            className="btn-send-otp mt-2"
                            onClick={handleSendOtp}
                            disabled={sendingOtp || checkingMobile || Boolean(activeSubscriptionInfo)}
                            title={
                              activeSubscriptionInfo
                                ? "Subscription already active for this mobile number"
                                : ""
                            }
                          >
                            {sendingOtp ? (
                              <>
                                <FaSpinner className="spinner-border spinner-border-sm me-1" />
                                Sending OTP...
                              </>
                            ) : activeSubscriptionInfo ? (
                              "Subscription Already Active"
                            ) : (
                              "Register / Send OTP"
                            )}
                          </button>
                        ) : (
                          /* OTP Verification Box */
                          <div className="otp-section mt-2 mb-3">
                            <div className="otp-title">
                              Enter 6-digit OTP sent to <strong>{formMobile}</strong>:
                            </div>
                            <input
                              type="text"
                              maxLength={6}
                              className="otp-input-control"
                              placeholder="••••••"
                              value={formOtp}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                setFormOtp(val);
                                setFormErrors((prev) => ({ ...prev, otp: "" }));
                              }}
                            />
                            {formErrors.otp && (
                              <div className="field-error mb-2">{formErrors.otp}</div>
                            )}

                            <div className="otp-actions">
                              <button
                                type="button"
                                className="btn-verify"
                                onClick={handleVerifyOtp}
                                disabled={verifyingOtp || formOtp.length < 4}
                              >
                                {verifyingOtp ? "Verifying..." : "Verify OTP"}
                              </button>

                              <button
                                type="button"
                                className="btn-resend"
                                onClick={handleResendOtp}
                                disabled={countdown > 0}
                              >
                                {countdown > 0 ? `Resend (${countdown}s)` : "Resend OTP"}
                              </button>

                              <button
                                type="button"
                                className="btn-edit"
                                onClick={handleEditDetails}
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Subscribe Now Button */}
                    <div className="mt-3 pt-3 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">Total Payable:</span>
                        <span className="fw-bold fs-5 text-primary">₹{finalPrice}</span>
                      </div>

                      <button
                        className="btn-subscribe w-100"
                        onClick={handleSubscribe}
                        disabled={!canBuy || buyingSubscription || Boolean(activeSubscriptionInfo)}
                        title={
                          activeSubscriptionInfo
                            ? "Subscription is already active for this mobile number"
                            : !canBuy
                            ? "Please fill details and verify OTP above to subscribe"
                            : "Proceed to pay"
                        }
                      >
                        {activeSubscriptionInfo ? (
                          <>
                            <FaExclamationTriangle size={13} /> Already Subscribed (Plan Active)
                          </>
                        ) : !canBuy ? (
                          <>
                            <FaLock size={13} /> Verify OTP to Buy Plan
                          </>
                        ) : buyingSubscription ? (
                          <>
                            <FaSpinner className="spinner-border spinner-border-sm me-1" />
                            Opening Razorpay...
                          </>
                        ) : (
                          `Subscribe Now (₹${finalPrice})`
                        )}
                      </button>

                      <div className="text-center mt-2">
                        <small className="text-muted" style={{ fontSize: "11px" }}>
                          🔒 100% Secure Checkout powered by Razorpay
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      <Footer />
    </SubscriptionDetailsPageContainer>
  );
};

export default SubscriptionDetailsPage;
